import { useMemo } from 'react';

import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useAtomValue } from 'jotai';

import type {
  HygieneInsightsResponse,
  HygieneTaskProduction,
  HygienistInsights,
  THygieneTaskCode,
} from '@/react-query/insights/types';
import { useHygieneInsightsData } from '@/react-query/insights/useGetHygieneInsightsData';
import { useGetPracticeGroups } from '@/react-query/insights/usePracticeGroups';
import { usePreferences } from '@/react-query/usePreferences';

import { endDateAtom, startDateAtom } from '../../atoms';
import { taskTypesToCodes } from '../taskTypeHelpers';

export const useGetFilteredHygieneData = () => {
  const startDate = useAtomValue(startDateAtom);
  const endDate = useAtomValue(endDateAtom);

  const { data: prefs } = usePreferences();

  const { data, isLoading } = useHygieneInsightsData({
    start: startOfDay(startDate),
    end: endOfDay(endDate),
    priorStart: startOfDay(
      subDays(
        startDate.getTime() - (endDate.getTime() - startDate.getTime()),
        1,
      ),
    ),
    priorEnd: endOfDay(subDays(startDate, 1)),
  });

  const { data: practiceGroups } = useGetPracticeGroups();

  const filteredData = useMemo(() => {
    const enabledTaskTypes = prefs?.insightsEnabledTasks;

    let processedData: HygieneInsightsResponse | undefined = data;

    if (
      enabledTaskTypes &&
      enabledTaskTypes.length > 0 &&
      processedData?.practices
    ) {
      // Convert task codes to task types for filtering
      const enabledTaskCodes = taskTypesToCodes(enabledTaskTypes);

      processedData = {
        ...processedData,
        practices: processedData.practices.map((practice) => ({
          ...practice,
          hygienists: practice.hygienists?.map((hygienist) => ({
            ...hygienist,
            tasks: Object.fromEntries(
              Object.entries(hygienist.tasks ?? {}).filter(([taskCode]) =>
                enabledTaskCodes.includes(taskCode as THygieneTaskCode),
              ),
            ) as Partial<{ [key in THygieneTaskCode]: HygieneTaskProduction }>,
          })) as HygienistInsights[],
        })),
      };
    }

    const practiceGroupFilter = practiceGroups?.data
      .find((group) => group.id === prefs?.enableInsightsPilotRegion)
      ?.practices.map((practice) => practice.practiceId);

    if (prefs?.enableInsightsPilotRegion && processedData?.practices) {
      processedData = {
        ...processedData,
        practices:
          processedData.practices.filter((practice) =>
            practiceGroupFilter?.includes(practice.practiceId),
          ) ?? [],
      };
    }
    return processedData;
  }, [
    data,
    practiceGroups?.data,
    prefs?.enableInsightsPilotRegion,
    prefs?.insightsEnabledTasks,
  ]);

  return { filteredData, isLoading };
};
